import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Group, Loader, Stack, Text, Tooltip } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import accounting from 'accounting';
import dayjs from 'dayjs';
import { fetchTransactionsForReceipt, fetchTransactionsForRentalPayment, deleteTransactionAssignment } from './receiptTenantTransactionsApi';
import TransactionDetailModal from '../neoverv/components/Module/General/CustomModal/TransactionDetailModal';
import AssignTransactionModal from '../neoverv/components/Module/General/CustomModal/AssignTransactionModal';
import CustomModal from '../neoverv/components/Module/General/CustomModal';
import ColorButton from '../neoverv/components/Module/General/Buttons/ColorButton';
import EmptyStateContainerForModals from './EmptyStateContainerForModals';
import { ViewTransactionIcon, GoToTransactionIcon, RemoveAssignmentIcon } from './TransactionActionIcons';
import PenIcon from '../assets/images/module/general/PenIcon.svg';
import Bank from '../assets/images/module/general/Bank.svg';
import RedCross from '../assets/images/module/general/RedCross.svg';
import RedTick from '../assets/images/module/general/RedTick.svg';

// Lists the bank transactions assigned to a receipt or a tenant's rental payment, per the
// "ReceiptTenantTransactions" ticket. totalAmount is accepted for backward compatibility with
// existing callers (ShowModal.js, UpdateTenantRentModal.jsx) but is no longer used here: real
// transactions carry their own real `amount`, unlike the old mock which had to fabricate one
// by splitting totalAmount across fake rows.
const TransactionsSection = ({ title, receiptId, tenantId, rentalPaymentId, rentalMonth, totalAmount, onAssignmentsChanged }) => {
  const history = useHistory();
  const propertyId = useSelector((state) => state.generalReducer.property?.id);
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [viewedTransaction, setViewedTransaction] = useState(null);
  const [editedTransaction, setEditedTransaction] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [transactionToRemove, setTransactionToRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  const removeAssignment = () => {
    setRemoving(true);
    deleteTransactionAssignment({ transactionId: transactionToRemove.id, receiptId, tenantId })
      .then(() => {
        showNotification({
          message: 'Zuordnung entfernt.',
          icon: <img src={RedTick} alt="red-tick" />,
          id: 'deleteTransactionReceiptAssignment'
        });
        setTransactionToRemove(null);
        setReloadKey((key) => key + 1);
        onAssignmentsChanged && onAssignmentsChanged();
      })
      .catch((err) => {
        console.error('TransactionsSection: failed to remove assignment', err);
        showNotification({
          message: 'Es ist ein Fehler aufgetreten. Bitte probieren Sie es erneut oder wenden Sie sich an den Support.',
          icon: <img src={RedCross} alt="red-cross" />,
          id: 'deleteTransactionReceiptAssignmentError'
        });
      })
      .finally(() => setRemoving(false));
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    const request = receiptId != null
      ? fetchTransactionsForReceipt({ propertyId, receiptId })
      : fetchTransactionsForRentalPayment({ propertyId, tenantId, rentalPaymentId, rentalMonth });
    request
      .then((result) => {
        if (active) {
          setTransactions(result);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error('TransactionsSection: failed to load transactions', err);
        if (active) {
          showNotification({
            message: 'Transaktionen konnten nicht geladen werden. Bitte versuchen Sie es später erneut.',
            icon: <img src={RedCross} alt="red-cross" />,
            id: 'transactionsSectionLoadError'
          });
          setTransactions([]);
          setLoading(false);
        }
      });
    return () => { active = false; };
  }, [propertyId, receiptId, tenantId, rentalPaymentId, rentalMonth, reloadKey]);

  return (
    <Stack gap={8}>
      {loading ? (
        <Group justify="center" py={8}>
          <Loader size="xs" />
        </Group>
      ) : transactions.length === 0 ? (
        <EmptyStateContainerForModals
          heading={'Keine zugeordneten Umsätze'}
          subheading={receiptId != null
            ? 'Ordnen Sie diesem Beleg die passenden Bankumsätze zu.'
            : 'Ordnen Sie dieser Mietzahlung die passenden Bankumsätze zu.'}
          icon={Bank}
          btnText={'Zu den Bankumsätzen'}
          btnHandler={() => history.push('/accounting/bank')}
        />
      ) : (
        <Stack gap={0}>
          {transactions.map((txn, index) => (
            // Padding only between rows, so the section heading's margin stays the distance to the first row.
            <Group key={txn.id} wrap="nowrap" align="flex-start" style={{ borderBottom: index < transactions.length - 1 ? '1px solid #EDEDF5' : 'none', paddingTop: index === 0 ? 0 : 8, paddingBottom: index < transactions.length - 1 ? 8 : 0 }}>
              <Stack gap={0} style={{ minWidth: 0, flex: 1 }}>
                <Text style={{ fontSize: 14, fontFamily: 'Inter, sans-serif', fontWeight: receiptId ? '500' : '400' }}>{txn.counterpartName}</Text>
                <Group wrap="nowrap" gap={6} style={{ minWidth: 0 }}>
                  {txn.purpose &&
                  <>
                    <Text style={{ fontSize: 12, fontFamily: 'Inter, sans-serif', color: '#727293', minWidth: 0 }} truncate="end">{txn.purpose}</Text>
                    <Text style={{ fontSize: 12, fontFamily: 'Inter, sans-serif', color: '#9A9AB5', flexShrink: 0 }}>-</Text>
                  </>}
                  <Text style={{ fontSize: 12, fontFamily: 'Inter, sans-serif', color: '#727293', whiteSpace: 'nowrap', flexShrink: 0 }}>{txn.bankBookingDate ? dayjs(txn.bankBookingDate).format('DD.MM.YYYY') : '-'}</Text>
                </Group>
              </Stack>
              <Text style={{ fontSize: 14, whiteSpace: 'nowrap', fontFamily: 'Inter, sans-serif', width: 100, textAlign: 'right', flexShrink: 0, height: '21px', fontWeight: receiptId ? '500' : '400' }}>
                {accounting.formatMoney(txn.amount, '€', 2, '.', ',', '%v %s')}
              </Text>
              <Group gap={6} wrap="nowrap" style={{ flexShrink: 0, width: 60, justifyContent: 'flex-end', height: '21px' }}>
                <Tooltip label="Details anzeigen" zIndex={99999}>
                  <ViewTransactionIcon aria-label="Details anzeigen" onClick={() => setViewedTransaction(txn)} />
                </Tooltip>
                {/* <Tooltip label="Zuordnung bearbeiten" zIndex={99999}>
                  <img
                    src={PenIcon}
                    alt="Zuordnung bearbeiten"
                    style={{ width: '12px', height: '12px', cursor: 'pointer' }}
                    onClick={() => setEditedTransaction(txn)}
                  />
                </Tooltip> */}
                <Tooltip label="Zur Transaktion gehen" zIndex={99999}>
                  <GoToTransactionIcon aria-label="Zur Transaktion gehen" onClick={() => history.push(`/accounting/bank?transactionIds=${txn.id}`)} />
                </Tooltip>
                <Tooltip label="Zuordnung entfernen" zIndex={99999}>
                  <RemoveAssignmentIcon aria-label="Zuordnung entfernen" onClick={() => setTransactionToRemove(txn)} />
                </Tooltip>
              </Group>
            </Group>
          ))}
        </Stack>
      )}
      {viewedTransaction &&
      <TransactionDetailModal
        isMobile={false}
        open={viewedTransaction !== null}
        transaction={viewedTransaction}
        renderTransactionAssigmentBlock={() => null}
        toggle={() => setViewedTransaction(null)}
      />}
      {editedTransaction &&
      <AssignTransactionModal
        edit
        open={editedTransaction !== null}
        toggle={(saved) => {
          setEditedTransaction(null);
          if (saved) {
            setReloadKey((key) => key + 1);
            onAssignmentsChanged && onAssignmentsChanged();
          }
        }}
        selectedTransaction={editedTransaction}
        selectedBankAccount={{ id: 'mock-bank-account' }}
        history={history}
        transactionSection={true}
      />}
      {transactionToRemove &&
      <CustomModal
        heading={'Zuordnung entfernen'}
        open={transactionToRemove !== null}
        toggle={() => !removing && setTransactionToRemove(null)}
      >
        <Stack>
          <span style={{ fontWeight: '400' }}>
            {`Möchten Sie die Zuordnung der Transaktion „${transactionToRemove.counterpartName || '-'}“ (${accounting.formatMoney(transactionToRemove.amount, '€', 2, '.', ',', '%v %s')}) ${receiptId != null ? 'zu diesem Beleg' : 'zu dieser Mietzahlung'} wirklich entfernen?`}
          </span>
          <Group justify="space-between" wrap="nowrap">
            <div>
              <ColorButton
                text={'Abbrechen'}
                className={'gray-btn'}
                onClick={() => !removing && setTransactionToRemove(null)}
              />
            </div>
            <div>
              <ColorButton
                text={'Zuordnung entfernen'}
                className={'red-btn'}
                showLoader={removing}
                onClick={() => !removing && removeAssignment()}
              />
            </div>
          </Group>
        </Stack>
      </CustomModal>}
    </Stack>
  );
};

export default TransactionsSection;
